import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type DependencyList,
  type RefObject,
} from "react";

type UseAutoScrollReturn = {
  /** Attach to the scrollable container element */
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  /** Whether the user has scrolled away from the bottom */
  showScrollButton: boolean;
  /** Scroll to the bottom and re-enable auto-scrolling */
  scrollToBottom(): void;
  /** Attach as the onScroll handler of the scrollable container */
  onScroll(): void;
  /** Reset auto-scroll state (e.g. when starting a new conversation) */
  resetAutoScroll(): void;
};

/**
 * Manages auto-scrolling for a scrollable container.
 *
 * Automatically scrolls to the bottom when content changes, unless the user
 * has manually scrolled away. A `showScrollButton` flag indicates when a
 * "scroll to bottom" affordance should be shown. A `ResizeObserver` ensures
 * the flag is re-evaluated when content dimensions change (e.g. a collapsible
 * section is toggled).
 *
 * @param deps - Re-evaluated whenever these dependencies change (e.g. messages array)
 * @returns Auto-scroll state and handlers to attach to the scrollable container
 */
export function useAutoScroll(deps: DependencyList): UseAutoScrollReturn {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef(true);
  const [showScrollButton, setShowScrollButton] = useState(false);

  const checkIfAtBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Use a tolerance so minor scroll jitter (e.g. trackpad inertia) doesn't disable auto-scroll
    const threshold = 50;
    // Remaining pixels below the visible viewport — 0 means fully scrolled to the bottom
    const isAtBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight <
      threshold;

    autoScrollRef.current = isAtBottom;
    setShowScrollButton(!isAtBottom);
  }, []);

  // Re-evaluate scroll button visibility when content size changes (e.g. reasoning collapse)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => checkIfAtBottom());
    for (const child of container.children) {
      observer.observe(child);
    }

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkIfAtBottom, ...deps]);

  const scrollToBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
      autoScrollRef.current = true;
      setShowScrollButton(false);
    }
  }, []);

  // Auto-scroll to bottom when content changes, unless the user scrolled away
  useLayoutEffect(() => {
    if (autoScrollRef.current) {
      scrollContainerRef.current?.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const resetAutoScroll = useCallback(() => {
    autoScrollRef.current = true;
    setShowScrollButton(false);
  }, []);

  return {
    scrollContainerRef,
    showScrollButton,
    scrollToBottom,
    onScroll: checkIfAtBottom,
    resetAutoScroll,
  };
}
